// TurnstileWidget — modo Managed (NO Invisible). Ver
// INSTRUCTIVO_TURNSTILE_APPS_FINAL.md: no se llama
// window.turnstile.execute(), no se oculta el contenedor con
// display:none, no se pasan los parámetros size/execution a render().
//
// El script de Cloudflare (?render=explicit) se carga UNA VEZ desde
// public/index.html — este componente solo renderiza el widget dentro
// de su contenedor cuando el script ya está listo.

import React, { useEffect, useRef, useState } from 'react';
import { Box, Alert } from '@mui/material';
import { obtenerTurnstileSiteKey } from '../services/ecolabAuth';

function esperarTurnstile(intentos = 50) {
  return new Promise((resolve, reject) => {
    const check = (n) => {
      if (window.turnstile) return resolve();
      if (n <= 0) return reject(new Error('Turnstile no cargó'));
      setTimeout(() => check(n - 1), 100);
    };
    check(intentos);
  });
}

/**
 * @param {(token: string) => void} onToken
 * @param {(err: any) => void} [onError]
 */
export default function TurnstileWidget({ onToken, onError }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelado = false;

    async function iniciar() {
      let siteKey;
      try {
        siteKey = await obtenerTurnstileSiteKey();
      } catch (err) {
        if (!cancelado) setError('No se pudo contactar a Ecolab para obtener la configuración de seguridad. Verifica tu conexión.');
        return;
      }

      try {
        await esperarTurnstile();
      } catch (err) {
        // El fetch a Ecolab SÍ funcionó (siteKey se obtuvo) — lo que
        // falló fue que window.turnstile nunca apareció. Casi siempre
        // es un bloqueador de anuncios/extensión de privacidad
        // bloqueando challenges.cloudflare.com, no un problema de
        // conexión con Ecolab ni con CoopNet.
        if (!cancelado) {
          setError(
            'El script de verificación de Cloudflare no cargó. Si tienes un bloqueador de anuncios o una ' +
            'extensión de privacidad activa, desactívala para este sitio (o prueba en una ventana de incógnito) y recarga.'
          );
        }
        return;
      }

      if (cancelado || !containerRef.current) return;

      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: 'login',
          // NO se pasan `size` ni `execution` — el widget está en modo Managed.
          callback: (token) => onToken(token),
          'error-callback': (code) => {
            console.warn('Turnstile error:', code);
            onError?.(code);
          },
          'expired-callback': () => {
            onToken('');
            if (widgetIdRef.current != null) window.turnstile.reset(widgetIdRef.current);
          },
        });
      } catch (err) {
        if (!cancelado) setError('No se pudo inicializar el widget de verificación. Recarga la página.');
      }
    }

    iniciar();

    return () => {
      cancelado = true;
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) return <Alert severity="error">{error}</Alert>;

  return <Box ref={containerRef} sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }} />;
}
